// src/pages/admin/SupplierPerformancePage.jsx
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { adminReportsApi } from '@/api/services'
import { Award, Star, TrendingUp, AlertTriangle } from 'lucide-react'

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`

function RatingStars({ rating }) {
  if (!rating) return <span className="text-xs text-gray-400">No ratings</span>
  return (
    <div className="flex items-center gap-1">
      <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
      <span className="text-sm font-bold">{rating}</span>
    </div>
  )
}

function FulfilmentBadge({ rate }) {
  const color = rate >= 90 ? 'bg-green-100 text-green-700'
    : rate >= 70 ? 'bg-yellow-100 text-yellow-700'
    : 'bg-red-100 text-red-700'
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{rate}%</span>
}

export default function SupplierPerformancePage() {
  const [days, setDays] = useState(90)
  const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0]
  const to = new Date().toISOString().split('T')[0]

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-perf', from, to],
    queryFn: () => adminReportsApi.supplierPerformance({ from, to }).then(r => r.data),
  })

  const suppliers = data?.data || []

  // Aggregate stats
  const totalRevenue = suppliers.reduce((s, x) => s + x.total_revenue, 0)
  const totalOrders = suppliers.reduce((s, x) => s + x.total_orders, 0)
  const avgFulfilment = suppliers.length
    ? (suppliers.reduce((s, x) => s + x.fulfilment_rate, 0) / suppliers.length).toFixed(1)
    : 0
  const lowPerformers = suppliers.filter(s => s.fulfilment_rate < 70).length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Award className="w-6 h-6 text-pink-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Supplier Performance</h1>
            <p className="text-xs text-gray-500">Scoreboard for the last {days} days</p>
          </div>
        </div>
        <div className="flex gap-1">
          {[30, 60, 90, 180].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${days === d ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Active Suppliers</p>
          <p className="text-2xl font-bold text-gray-900">{suppliers.length}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Revenue</p>
          <p className="text-2xl font-bold text-gray-900">{fmt(totalRevenue)}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Avg Fulfilment</p>
          <p className="text-2xl font-bold text-gray-900">{avgFulfilment}%</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Low Performers</p>
          </div>
          <p className="text-2xl font-bold text-red-600 flex items-center gap-2">
            {lowPerformers}
            {lowPerformers > 0 && <AlertTriangle className="w-5 h-5" />}
          </p>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : suppliers.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No supplier data for this period</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-600">
                <th className="px-4 py-3 font-medium w-8">#</th>
                <th className="px-4 py-3 font-medium">Supplier</th>
                <th className="px-4 py-3 font-medium text-center">Orders</th>
                <th className="px-4 py-3 font-medium text-center">Delivered</th>
                <th className="px-4 py-3 font-medium text-center">Cancelled</th>
                <th className="px-4 py-3 font-medium text-center">Fulfilment</th>
                <th className="px-4 py-3 font-medium text-right">Revenue</th>
                <th className="px-4 py-3 font-medium text-center">Rating</th>
                <th className="px-4 py-3 font-medium text-center">Reviews</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {suppliers.map((s, i) => (
                <tr key={s.id} className={`hover:bg-gray-50 ${s.fulfilment_rate < 70 ? 'bg-red-50/50' : ''}`}>
                  <td className="px-4 py-3">
                    {i < 3 ? (
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                        i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-gray-400' : 'bg-amber-700'
                      }`}>{i + 1}</span>
                    ) : (
                      <span className="text-gray-400 text-xs">{i + 1}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{s.name}</td>
                  <td className="px-4 py-3 text-center">{s.total_orders}</td>
                  <td className="px-4 py-3 text-center text-green-600">{s.delivered_orders}</td>
                  <td className="px-4 py-3 text-center text-red-500">{s.cancelled_orders}</td>
                  <td className="px-4 py-3 text-center"><FulfilmentBadge rate={s.fulfilment_rate} /></td>
                  <td className="px-4 py-3 text-right font-bold">{fmt(s.total_revenue)}</td>
                  <td className="px-4 py-3 text-center"><RatingStars rating={s.avg_rating} /></td>
                  <td className="px-4 py-3 text-center text-gray-500">{s.review_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
